import { CachedPrefixAchievement } from 'services/home-feed.service';
import { FormatNumber } from 'utils/helpers';
import { GetTaskLevelLabel } from 'utils/strings';

import { PersonAvatar, rostoDoAutor } from 'components/person-avatar';

import { iniciaisDoNomeCompleto } from '../feed-calculos';
import { ROTA_DAS_REALIZACOES_DO_PREFIXO } from '../rotas-do-feed';
import CabecalhoDeSecao from './CabecalhoDeSecao';
import { FOCO_VISIVEL } from 'utils/foco';

// Cor e inclinação de cada cartão, como no design.
export const ESTILO_DO_CARTAO = [
  'bg-brand-green-light -rotate-[1.3deg]',
  'bg-brand-purple-light rotate-[0.9deg]',
  'bg-brand-cyan-light -rotate-[0.7deg]',
];

/** Uma realização do prefixo: fase, pontos, título, rosto do dono e "{unidade} · {status}" (REDE-05, AJ3-10). */
export function CartaoDeRealizacao({ item, estilo }: { item: CachedPrefixAchievement; estilo: string }) {
  return (
    <li
      tabIndex={0}
      className={`flex flex-col gap-[13px] rounded-[20px] px-[22px] pb-[22px] pt-5 shadow-jj-01 transition duration-[180ms] hover:-translate-y-[5px] hover:shadow-[0_14px_30px_rgba(0,45,75,0.18)] motion-reduce:hover:translate-y-0 ${FOCO_VISIVEL} ${estilo}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.06em] text-content opacity-[0.68]">
          {item.faseTitle}
        </span>
        <span className="ml-auto whitespace-nowrap rounded-jj-pill bg-brand-blue px-3 py-[5px] font-display text-[11px] font-bold text-brand-white">
          {FormatNumber(item.total ?? 0, false)} pts
        </span>
      </div>
      <h3 className="m-0 font-display text-[17px] font-extrabold leading-[1.2] text-brand-blue-dark">{item.titulo}</h3>
      <div className="mt-auto flex items-center gap-[9px]">
        {/* Rosto do dono: foto ou avatar montado; sem eles, as iniciais (AJH-53). */}
        <span className="shrink-0 rounded-full shadow-jj-01">
          <PersonAvatar
            {...rostoDoAutor(item)}
            initials={iniciaisDoNomeCompleto(item.author || item.nome_uor)}
            size={30}
            initialsClassName="bg-surface-0 font-display text-[11px] font-extrabold text-content"
          />
        </span>
        <span className="text-[12px] text-content opacity-[0.78]">
          {item.author && <b className="font-display font-bold">{item.author} · </b>}
          {item.nome_uor} · {GetTaskLevelLabel(item.status ?? '')}
        </span>
      </div>
    </li>
  );
}

interface UltimasRealizacoesProps {
  prefixo?: number;
  itens: CachedPrefixAchievement[];
}

/**
 * "Últimas realizações destaque" do prefixo (REDE-05). O cache não traz comportamento, data nem
 * autor (REDE-L03): a linha de cima mostra a fase e a de baixo a unidade, como a Home.
 */
export default function UltimasRealizacoes({ prefixo, itens }: UltimasRealizacoesProps) {
  if (!itens.length) return null;

  return (
    <section aria-labelledby="titulo-ultimas-realizacoes" className="mt-[26px] flex flex-col gap-3.5">
      <CabecalhoDeSecao
        id="titulo-ultimas-realizacoes"
        sobretitulo={`Ainda no prefixo ${prefixo ?? ''}`}
        titulo="Últimas realizações destaque"
        subtitulo="O que a sua turma cadastrou por aqui recentemente."
        link={{ para: ROTA_DAS_REALIZACOES_DO_PREFIXO, rotulo: 'Ver todas do prefixo' }}
      />

      <ul className="m-0 grid list-none grid-cols-1 gap-5 px-1 pb-3 pt-2 md:grid-cols-3">
        {itens.slice(0, 3).map((item, indice) => (
          <CartaoDeRealizacao key={item._id} item={item} estilo={ESTILO_DO_CARTAO[indice % ESTILO_DO_CARTAO.length]} />
        ))}
      </ul>
    </section>
  );
}
